import { DrawerHeader } from "../../../../../components/drawer/drawer-header";
import { memo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useProjectNavigate } from "../../../../../common/utils/useProjectNavigate";
import moment from "moment";
import { Drawer } from "../../../../../components/drawer/drawer";
import InfinityTable from "../../../../../components/infinity-table/infinity-table";
import ApiAnalytics from "../../../../../api/analytics";
import InfoCardWrapper from "../../../../../components/info-card/info-card-wrapper";
import StatisticChart from "../../../../../components/statistic-chart/statistic-chart";
import DropdownFilters from "../../../../../components/dropdown-filters/dropdown-filters";
import formatAnswerValue from "../../../../../common/utils/formatAnswerValue";
import { Tabs } from "antd";

const AnalyticsCardInside = memo(
  ({
    analytics,
    close,
    messageTypes,
    subjects,
    cohorts,
    initialPeriodFilter,
    initialCohortsFilter
  }) => {
    const [filters, setFilters] = useState(() => {
      const initialFilters = {};

      if (initialCohortsFilter?.cohortIds) {
        initialFilters.cohortIds = initialCohortsFilter?.cohortIds;
      }

      if (initialPeriodFilter?.period) {
        initialFilters.period = initialPeriodFilter?.period;
      }

      return initialFilters;
    });
    const filterItems = [
      { name: "Date peroid", id: "period", period: true },
      { name: "Subject ID", id: "subjectIds", children: subjects },
      { name: "Cohorts", id: "cohortIds", children: cohorts },
    ];
    const [localAnalytics, setLocalAnalytics] = useState(analytics);
    const { t } = useTranslation();
    const navigate = useProjectNavigate();

    const fetchFilteredAnalytics = (currentFilters) => {
      ApiAnalytics.getScenarioAnalyticsById(analytics.id, {
        ...currentFilters,
        createdAtEnd: currentFilters.period?.[currentFilters.period.length - 1]?.end,
        createdAtStart: currentFilters.period?.[currentFilters.period.length - 1]?.start,
      }).then((response) => {
        const updatedAnalytics = {
          ...analytics,
          ...response.data.question,
          SubjectScenarioResponses: response.data.question.SubjectScenarioResponses || [],
        };
        setLocalAnalytics(updatedAnalytics);
      });
    };

    useEffect(() => {
      if (initialPeriodFilter) {
        if (filters.period !== initialPeriodFilter) {
          const updatedFilters = { ...filters, period: initialPeriodFilter };
          setFilters(updatedFilters);
        }
      }
    }, [initialPeriodFilter]);

    const transformAnalyticsToData = (analytics) => {
      return [
        {
          title: analytics.respondedPercentage || 0,
          subtitle: t("analytics.card.adherence_rate"),
          isPercent: true,
        },
        {
          title: analytics.respondedCount || 0,
          subtitle: t("analytics.card.responded"),
          isPercent: false,
        },
        {
          title: analytics.pendingCount || 0,
          subtitle: t("analytics.card.pending"),
          isPercent: false,
        },
        {
          title: analytics.noRespondedCount || 0,
          subtitle: t("analytics.card.no_response"),
          isPercent: false,
        }
      ];
    };

    const data = transformAnalyticsToData(localAnalytics);

    const copyId = () => {
      navigator.clipboard.writeText(localAnalytics.internalId)
    };

    const getMessageNameById = (id) => {
      const message = messageTypes.find((type) => type.id === id);
      return message ? message.name : null;
    };

    const updateFilterValue = (filter) => {
      setFilters(filter);
    };

    const toSubjectPage = (subjectId) => {
      navigate(`/subject-management/subject/${subjectId}#profile`);
    };

    useEffect(() => {
      fetchFilteredAnalytics({
        ...filters,
        period: filters?.period?.period || filters?.period,
      });
    }, [filters]);

    const columnsResponded = [
      {
        title: t("analytics.table.subjectId"),
        dataIndex: "subjectId",
        key: "subjectId",
        render: (_, analytics) => <span>{analytics.Subject.displayId}</span>,
        width: "20%",
      },
      {
        title: t("analytics.table.response"),
        dataIndex: "response",
        key: "response",
        render: (_, analytics) => (
          <span>
            {analytics.response?.skipped
              ? "Skipped"
              : formatAnswerValue(analytics.answer)}
          </span>
        ),
        width: "40%",
      },
      {
        title: t("analytics.table.completion_date"),
        dataIndex: "completion_date",
        key: "completion_date",
        render: (_, analytics) =>
          analytics.completedAt
            ? `${moment(analytics.completedAt).utc().format("DD MMM YYYY, HH:mm")}, UTC`
            : "N/A",
        width: "40%",
      },
    ];

    const columnsPendingAndNoResponded = [
      {
        title: t("analytics.table.subjectId"),
        dataIndex: "subjectId",
        key: "subjectId",
        render: (_, analytics) => <span>{analytics.Subject.displayId}</span>,
        width: "50%",
      },
      {
        title: t("analytics.table.date"),
        dataIndex: "createdAt",
        key: "createdAt",
        render: (_, analytics) =>
          analytics.createdAt
            ? `${moment(analytics.createdAt).utc().format("DD MMM YYYY, HH:mm")}, UTC`
            : "N/A",
        width: "50%",
      },
    ];
    return (
      <>
        <DrawerHeader
          badge={localAnalytics?.Scenario?.title}
          title={localAnalytics.title}
          subtitle={getMessageNameById(localAnalytics.questionType)}
          onClose={close}
          menu={
            {
              items: [
                {
                  label: "Copy ID",
                  onClick: copyId
                }
              ]
            }
          }
        />
        <div style={{ paddingBottom: 16 }} />
        <DropdownFilters
          filtersBy={filterItems}
          updateFilterValue={updateFilterValue}
          filter={{ ...initialPeriodFilter, ...initialCohortsFilter }}
          solidBorder={false}
        />
        <div style={{ paddingBottom: 20 }} />
        <InfoCardWrapper data={data} />
        <div style={{ paddingBottom: 12 }} />
        {localAnalytics.responseStats && (
          <StatisticChart
            data={localAnalytics.responseStats}
          />
        )}
        <div style={{ paddingBottom: 12 }} />
        <div className="analytics-tabs">
          <Tabs
            defaultActiveKey="1"
            // antd 6 takes its tabs as a list rather than as TabPane children.
            items={[
              {
                key: "1",
                label: t("analytics.card.responded"),
                children: (
                  <InfinityTable
                    data={localAnalytics.responded}
                    tableColumns={columnsResponded}
                    onHandleRowClick={(record) => toSubjectPage(record.subjectId)}
                  />
                ),
              },
              {
                key: "2",
                label: t("analytics.card.pending"),
                children: (
                  <InfinityTable
                    data={localAnalytics.pending}
                    tableColumns={columnsPendingAndNoResponded}
                    onHandleRowClick={(record) => toSubjectPage(record.subjectId)}
                  />
                ),
              },
              {
                key: "3",
                label: t("analytics.card.no_response"),
                children: (
                  <InfinityTable
                    data={localAnalytics.noResponded}
                    tableColumns={columnsPendingAndNoResponded}
                    onHandleRowClick={(record) => toSubjectPage(record.subjectId)}
                  />
                ),
              },
            ]}
          />
        </div>
      </>
    );
  }
);

const AnalyticsCard = ({
  analytics,
  isOpen,
  close,
  copy,
  messageTypes,
  subjects,
  cohorts,
  initialPeriodFilter,
  initialCohortsFilter
}) => {
  return (
    <Drawer open={isOpen} size={800} closable={false} onClose={close}>
      <div style={{ paddingBottom: 48 }}>
        {isOpen && (
          <AnalyticsCardInside
            analytics={analytics}
            close={close}
            messageTypes={messageTypes}
            subjects={subjects}
            cohorts={cohorts}
            initialPeriodFilter={initialPeriodFilter}
            initialCohortsFilter={initialCohortsFilter}
          />
        )}
      </div>
    </Drawer>
  );
};

export default AnalyticsCard;
